import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// ── Lightweight computed entry (no raw ratings, keeps payload small) ──────────
export interface RatingResultEntry {
  targetId: string;
  targetName: string;
  targetTeam: 'black' | 'white';
  totalScore: number;   // sum of all votes
  avgScore: number;     // mean
  voteCount: number;
}

export interface RatingResultPayload {
  id: string;
  title: string;
  votingType: 'team' | 'individual';
  archivedAt: number;
  totalVoters: number;
  entries: RatingResultEntry[];  // pre-computed — no raw ratings blob
}

const CHANNEL_NAME = 'srs-rating-result-broadcast';
const WINDOW_EVENT = 'srs-rating-result';

export function useRatingResultBroadcast() {
  const [pendingResult, setPendingResult] = useState<RatingResultPayload | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Same-device listener (Supabase broadcast doesn't self-echo by default)
  useEffect(() => {
    const handler = (e: Event) =>
      setPendingResult((e as CustomEvent<RatingResultPayload>).detail);
    window.addEventListener(WINDOW_EVENT, handler);
    return () => window.removeEventListener(WINDOW_EVENT, handler);
  }, []);

  // Cross-device receiver via Supabase Realtime Broadcast
  useEffect(() => {
    const ch = supabase.channel(CHANNEL_NAME, {
      config: { broadcast: { ack: false } },
    });
    ch.on('broadcast', { event: 'rating_result' }, (msg) => {
      setPendingResult(msg.payload as RatingResultPayload);
    }).subscribe();
    channelRef.current = ch;
    return () => {
      supabase.removeChannel(ch);
      channelRef.current = null;
    };
  }, []);

  /** Fire window event (same device) + Supabase broadcast (other devices) */
  const publishResult = useCallback((payload: RatingResultPayload) => {
    // Immediately visible on sender's device
    window.dispatchEvent(new CustomEvent(WINDOW_EVENT, { detail: payload }));
    // Push to all other connected devices
    channelRef.current?.send({
      type: 'broadcast',
      event: 'rating_result',
      payload,
    });
  }, []);

  const clearResult = useCallback(() => setPendingResult(null), []);

  return { publishResult, pendingResult, clearResult };
}
